import React, { useMemo } from 'react';
import { Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import { DropPoint } from '@m4a3/api-client';
import { RiskAssessment } from '../../utils/riskUtils';
import type { ClickMode } from '../../models/click-mode';
import { MAP_CONFIG, RISK_COLORS } from '../../constants/mapConfig';

export interface RiskAssessmentOverlayProps {
  selectedDropPointId: string | null;
  dropPoints: DropPoint[];
  newPinLocation: L.LatLng | null;
  riskAssessment: RiskAssessment | null;
  isLoadingRiskAssessment: boolean;
  clickMode: ClickMode;
  onPinMove: (newPosition: L.LatLng) => void;
}

const RiskAssessmentOverlay = ({
  selectedDropPointId,
  dropPoints,
  newPinLocation,
  riskAssessment,
  isLoadingRiskAssessment,
  clickMode,
  onPinMove,
}: RiskAssessmentOverlayProps): JSX.Element => {
  // Get circle colors based on risk assessment
  const getCircleColors = (assessment: RiskAssessment | null, isLoading: boolean) => {
    // Show white circle while loading
    if (isLoading) {
      return { 
        color: RISK_COLORS.UNKNOWN, 
        fillColor: RISK_COLORS.UNKNOWN, 
        weight: 3,
        fillOpacity: 0.2 
      };
    }

    if (!assessment) {
      return { 
        color: RISK_COLORS.UNKNOWN, 
        fillColor: RISK_COLORS.UNKNOWN, 
        weight: 3,
        fillOpacity: 0.2 
      };
    }

    // Border color based on building risk
    let strokeColor = RISK_COLORS.UNKNOWN; // Default white for no result
    if (assessment.buildingRisk === 'HIGH') {
      strokeColor = RISK_COLORS.HIGH; // Red border for near building
    } else if (assessment.buildingRisk === 'LOW') {
      strokeColor = RISK_COLORS.LOW; // Green border for clear area
    }

    // Fill color based on ground risk (height data)
    let fillColor = RISK_COLORS.UNKNOWN; // Default white for no result
    if (assessment.groundRisk === 'HIGH') {
      fillColor = RISK_COLORS.HIGH; // Red fill for unsafe (obstacles detected ≥ 1.0m)
    } else if (assessment.groundRisk === 'LOW') {
      fillColor = RISK_COLORS.LOW; // Green fill for safe (< 1.0m height)
    }

    return { 
      color: strokeColor, 
      fillColor: fillColor, 
      weight: 3,
      fillOpacity: 0.2 
    };
  };

  const circleColors = useMemo(() => 
    getCircleColors(riskAssessment, isLoadingRiskAssessment), 
    [riskAssessment, isLoadingRiskAssessment]
  );

  const selectedDropPoint = useMemo(() => {
    if (!selectedDropPointId || !Array.isArray(dropPoints)) return null;
    return dropPoints.find(dp => dp.id === selectedDropPointId) || null;
  }, [selectedDropPointId, dropPoints]);

  return (
    <>
      {/* Assessment Circle for selected drop point */}
      {selectedDropPoint && riskAssessment && (
        <Circle
          center={[selectedDropPoint.latitude, selectedDropPoint.longitude]}
          radius={MAP_CONFIG.RISK_ASSESSMENT_RADIUS} // 3 meter radius for assessment
          {...circleColors}
          opacity={0.8}
        />
      )}

      {/* New Pin Marker and Assessment Circle */}
      {newPinLocation && clickMode === 'add-drop-point' && (
        <>
          {/* Assessment Circle */}
          <Circle
            key={`new-pin-circle-${newPinLocation.lat}-${newPinLocation.lng}-${riskAssessment?.buildingRisk}-${riskAssessment?.groundRisk}-${isLoadingRiskAssessment}`}
            center={[newPinLocation.lat, newPinLocation.lng]}
            radius={MAP_CONFIG.RISK_ASSESSMENT_RADIUS} // 3 meter radius for assessment
            {...circleColors}
            opacity={0.8}
          />
          
          {/* Draggable Pin Marker */}
          <Marker
            position={[newPinLocation.lat, newPinLocation.lng]}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const position = marker.getLatLng();
                onPinMove(position);
              },
              click: () => {
                // Allow clicking on marker without triggering map click
              }
            }}
            icon={L.divIcon({
              html: `<div style="
                background-color: ${riskAssessment?.canSave ? RISK_COLORS.LOW : RISK_COLORS.HIGH};
                border: 2px solid white;
                border-radius: 50%;
                width: 20px;
                height: 20px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                ${isLoadingRiskAssessment ? 'animation: pulse 1s infinite;' : ''}
              "></div>`,
              className: 'custom-pin-marker',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            })}
          />
        </>
      )}
    </>
  );
};

export default React.memo(RiskAssessmentOverlay);
